type BaseJsonLdInput = {
  siteName: string;
  canonicalDomain: string;
  canonicalUrl: string;
  description: string;
  ogImage?: string | null;
  appCategory?: string;
  appName?: string;
  featureList?: string[];
};

/**
 * Conservative, machine-readable structured data to help search engines and LLMs
 * understand what CineRig is. Avoids unverifiable claims (ratings, pricing, etc.).
 */
export function buildBaseJsonLd(input: BaseJsonLdInput): Array<Record<string, unknown>> {
  const canonicalDomain = stripTrailingSlash(input.canonicalDomain);
  const canonicalUrl = input.canonicalUrl;
  const siteName = input.siteName;

  const organizationId = `${canonicalDomain}/#organization`;
  const websiteId = `${canonicalDomain}/#website`;
  const appId = `${canonicalDomain}/#app`;

  const organization: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': organizationId,
    name: siteName,
    url: canonicalDomain,
    ...(input.ogImage ? { logo: input.ogImage } : {}),
  };

  const website: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': websiteId,
    url: canonicalDomain,
    name: siteName,
    publisher: { '@id': organizationId },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${canonicalDomain}/gallery?search={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const app: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    '@id': appId,
    name: input.appName || siteName,
    url: canonicalUrl,
    description: input.description,
    applicationCategory: input.appCategory || 'MultimediaApplication',
    operatingSystem: 'Web',
    publisher: { '@id': organizationId },
    ...(input.featureList && input.featureList.length
      ? { featureList: input.featureList }
      : {}),
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      // Note: pricing page may include paid plans; this simply indicates free access exists.
      availability: 'https://schema.org/InStock',
    },
  };

  return [organization, website, app];
}

function stripTrailingSlash(url: string) {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}
